import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Hardcoded data for campaigns
const campaignsData = [
  { 
    id: 1, 
    name: 'Q3 Security Awareness', 
    status: 'completed',
    startDate: '2023-07-15',
    endDate: '2023-07-22',
    targets: 240,
    opened: 218,
    clicked: 45,
    reported: 173, 
  },
  { 
    id: 2, 
    name: 'Password Reset Simulation', 
    status: 'active',
    startDate: '2023-10-10',
    endDate: '2023-10-17',
    targets: 256,
    opened: 220,
    clicked: 62,
    reported: 158,
  },
  { 
    id: 3, 
    name: 'Invoice Phishing Test', 
    status: 'scheduled',
    startDate: '2023-11-05',
    endDate: '2023-11-12',
    targets: 240,
    opened: 0,
    clicked: 0,
    reported: 0, 
  },
];

const CampaignManagement = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  
  useEffect(() => {
    // Simulate API call to fetch campaigns
    setTimeout(() => {
      setCampaigns(campaignsData);
      setIsLoading(false);
    }, 1000);
  }, []);
  
  const filteredCampaigns = filter === 'all' 
    ? campaigns 
    : campaigns.filter(campaign => campaign.status === filter);
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Campaign Management</h1>
        <Link to="/admin/campaigns/new" className="btn btn-primary">
          Create Campaign
        </Link>
      </div>
      
      <div className="mb-6 flex space-x-2">
        <button 
          onClick={() => setFilter('all')}
          className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline'}`}
        >
          All
        </button>
        <button 
          onClick={() => setFilter('active')}
          className={`btn ${filter === 'active' ? 'btn-primary' : 'btn-outline'}`}
        >
          Active
        </button>
        <button 
          onClick={() => setFilter('scheduled')}
          className={`btn ${filter === 'scheduled' ? 'btn-primary' : 'btn-outline'}`}
        >
          Scheduled
        </button>
        <button 
          onClick={() => setFilter('completed')}
          className={`btn ${filter === 'completed' ? 'btn-primary' : 'btn-outline'}`}
        >
          Completed
        </button>
      </div>
      
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Period
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Targets
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Results
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCampaigns.map((campaign) => (
                <tr key={campaign.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                      ${campaign.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : campaign.status === 'scheduled' 
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                      {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {campaign.startDate} to {campaign.endDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {campaign.targets}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {campaign.status !== 'scheduled' ? (
                      <div className="text-sm">
                        <div className="flex items-center">
                          <span className="mr-2 text-gray-500">Opened:</span>
                          <span className="font-medium">{campaign.opened} ({Math.round((campaign.opened / campaign.targets) * 100)}%)</span>
                        </div>
                        <div className="flex items-center">
                          <span className="mr-2 text-gray-500">Clicked:</span>
                          <span className="font-medium text-red-600">{campaign.clicked} ({Math.round((campaign.clicked / campaign.targets) * 100)}%)</span>
                        </div>
                        <div className="flex items-center">
                          <span className="mr-2 text-gray-500">Reported:</span>
                          <span className="font-medium text-green-600">{campaign.reported} ({Math.round((campaign.reported / campaign.targets) * 100)}%)</span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">Not started</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <Link to={`/admin/campaigns/${campaign.id}`} className="text-primary-600 hover:text-primary-900 mr-4">
                      View
                    </Link>
                    {campaign.status === 'scheduled' && (
                      <button className="text-red-600 hover:text-red-900">
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredCampaigns.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No campaigns found matching the selected filter.</p>
          </div>
        )}
      </div>
      
      {/* Campaign Creation Form Preview */}
      <div className="card mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Create a New Campaign</h2>
        
        <div className="bg-blue-50 p-4 rounded-md mb-6">
          <p className="text-sm text-blue-700">
            Create effective phishing campaigns to test your organization's security awareness. 
            Our template library includes a variety of scenarios based on real-world phishing attacks.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border border-gray-200 rounded-md p-4 hover:border-primary-500 hover:shadow-md transition-all">
            <h3 className="font-medium text-gray-900 mb-2">Basic Campaign</h3>
            <p className="text-sm text-gray-600 mb-4">
              A simple campaign with pre-defined templates. Perfect for getting started.
            </p>
            <Link to="/admin/campaigns/new?type=basic" className="text-sm text-primary-600 hover:text-primary-800 font-medium">
              Start Basic Campaign →
            </Link>
          </div>
          
          <div className="border border-gray-200 rounded-md p-4 hover:border-primary-500 hover:shadow-md transition-all">
            <h3 className="font-medium text-gray-900 mb-2">Advanced Campaign</h3>
            <p className="text-sm text-gray-600 mb-4">
              Advanced targeting options with customizable templates and scheduling.
            </p>
            <Link to="/admin/campaigns/new?type=advanced" className="text-sm text-primary-600 hover:text-primary-800 font-medium">
              Start Advanced Campaign →
            </Link>
          </div>
          
          <div className="border border-gray-200 rounded-md p-4 hover:border-primary-500 hover:shadow-md transition-all">
            <h3 className="font-medium text-gray-900 mb-2">Clone Existing</h3>
            <p className="text-sm text-gray-600 mb-4">
              Create a new campaign based on a previous one with similar settings.
            </p>
            <Link to="/admin/campaigns/new?type=clone" className="text-sm text-primary-600 hover:text-primary-800 font-medium">
              Clone a Campaign →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignManagement; 